import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { openProDrawer } from "@/components/ProDrawer";
import { isGroupLimitError } from "@/lib/group-limits";
import { AppShell } from "@/components/AppShell";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { getInvite, acceptInvite } from "@/lib/groups.functions";

export const Route = createFileRoute("/comunidades/convite/$token")({
  head: () => ({ meta: [{ title: "Convite — Viny Club" }] }),
  component: ConvitePage,
});

function ConvitePage() {
  const { token } = Route.useParams();
  const { isAuthenticated, loading } = useCurrentUser();
  const navigate = useNavigate();
  const getFn = useServerFn(getInvite);
  const acceptFn = useServerFn(acceptInvite);

  const q = useQuery({
    queryKey: ["invite", token],
    queryFn: () => getFn({ data: { token } }),
  });

  const mut = useMutation({
    mutationFn: () => acceptFn({ data: { token } }),
    onSuccess: () => {
      toast.success("Você entrou no grupo!");
      if (q.data?.groups?.slug) {
        navigate({ to: "/comunidades/$slug", params: { slug: q.data.groups.slug } });
      }
    },
    onError: (e: Error) => {
      toast.error(e.message);
      if (isGroupLimitError(e)) openProDrawer();
    },
  });

  if (loading || q.isLoading) return <AppShell><div className="p-8 text-center text-sm text-muted-foreground">Carregando convite...</div></AppShell>;
  if (!q.data) {
    return (
      <AppShell>
        <div className="mx-auto max-w-md px-4 py-20 text-center">
          <h2 className="mb-2 text-xl font-semibold">Convite inválido</h2>
          <p className="text-sm text-muted-foreground">Este convite não existe ou expirou.</p>
        </div>
      </AppShell>
    );
  }
  const g = q.data.groups;
  const expired = q.data.expires_at && new Date(q.data.expires_at) < new Date();
  const exhausted = q.data.max_uses && q.data.uses >= q.data.max_uses;

  return (
    <AppShell>
      <div className="mx-auto max-w-md px-4 py-12">
        <div className="overflow-hidden rounded-2xl border border-[color:var(--hairline)] bg-surface/40">
          <div className="relative h-32 w-full overflow-hidden bg-gradient-to-br from-primary/30 via-surface to-surface">
            {g?.banner_url && <img loading="lazy" decoding="async" src={g.banner_url} alt="" className="h-full w-full object-cover" />}
          </div>
          <div className="px-6 pb-6 pt-4 text-center">
            <div className="-mt-12 mb-3 inline-block">
              <div className="h-20 w-20 overflow-hidden rounded-2xl border-4 border-background bg-surface">
                {g?.avatar_url ? (
                  <img loading="lazy" decoding="async" src={g.avatar_url} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="grid h-full w-full place-items-center text-2xl font-semibold text-muted-foreground">{g?.name?.[0]?.toUpperCase()}</div>
                )}
              </div>
            </div>
            <h1 className="font-display text-xl font-semibold">{g?.name}</h1>
            <p className="text-xs text-muted-foreground">{g?.member_count} membros</p>
            {g?.description && <p className="mt-3 text-sm text-muted-foreground">{g.description}</p>}

            <div className="mt-6">
              {expired || exhausted ? (
                <p className="text-sm text-destructive">Convite {expired ? "expirado" : "esgotado"}.</p>
              ) : !isAuthenticated ? (
                <button onClick={() => navigate({ to: "/auth" })} className="w-full rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground">
                  Entrar para aceitar
                </button>
              ) : (
                <button
                  onClick={() => mut.mutate()}
                  disabled={mut.isPending}
                  className="w-full rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground disabled:opacity-50"
                >
                  {mut.isPending ? "Entrando..." : "Aceitar convite"}
                </button>
              )}
            </div>
            <Link to="/comunidades" className="mt-3 inline-block text-xs text-muted-foreground hover:text-foreground">Voltar aos grupos</Link>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
